import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import type { Transition } from 'history';
import { Button } from 'ncoded-component-library';
import confirm from 'modules/confirm';
import { useBlocker } from 'hooks/useBlocker';
import useObjectUrl from 'hooks/useObjectUrl';
import AgreementPageLayout from 'components/AgreementPageLayout';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  AGREEMENT_ERROR_CODES,
  ActivateAgreementBody,
  AgreementField,
} from 'models/Agreement';
import { isPdf } from 'utils';
import {
  getAgreementErrorCode,
  getAgreementInvalidFieldIds,
  getIsAgreementFileTooLarge,
  getIsLabelInvalidError,
  getIsSignatureRequiredError,
} from 'agreementUtils';
import AgreementFieldEditor from './components/AgreementFieldEditor';
import AgreementDocumentPanel from './components/AgreementDocumentPanel';
import AgreementPdfDropzone from './components/AgreementPdfDropzone';
import {
  AGREEMENT_FILE_ERROR_KEYS,
  AGREEMENT_FILE_LIMIT_PARAMS,
  AGREEMENT_SETUP_EXITS,
  AgreementSetupExit,
  getCanActivateAgreement,
  getIsAgreementFileError,
  getIsAgreementPageCountValid,
} from './utils';

import './AgreementSetup.styles.scss';
import './AgreementSetup.styles.responsive.scss';

type AgreementSetupProps = {
  className?: string;
  backPath?: string;
  backLabel?: string;
  isDisabled?: boolean;
  initialFile?: File;
  onActivate: (body: ActivateAgreementBody) => Promise<unknown>;
  onActivated: () => void;
  onClose?: () => void;
  onDocusignConnected?: () => void;
};

const AgreementSetup: React.FC<AgreementSetupProps> = (props) => {
  const {
    className,
    backPath,
    backLabel,
    isDisabled: isDisabledProp,
    initialFile,
    onActivate,
    onActivated,
    onClose,
    onDocusignConnected,
  } = props;

  const { popError, popServerError } = useContext(
    PopNotificationsContext,
  );

  const { t } = useTranslation();

  const [file, setFile] = useState<File>();
  const [fields, setFields] = useState<AgreementField[]>([]);
  const [invalidFieldIds, setInvalidFieldIds] = useState<string[]>([]);
  const [isActivating, setIsActivating] = useState(false);
  const [exit, setExit] = useState<AgreementSetupExit>();

  const fileUrl = useObjectUrl(file);

  const isDisabled = isDisabledProp || isActivating;

  const classes = classNames('agreement-setup', className);

  const confirmLeave = useCallback(
    () =>
      confirm({
        title: t('agreementLeaveSetupTitle'),
        content: t('agreementLeaveSetupContent'),
        submitBtnText: t('leave'),
        cancelBtnText: t('keepEditing'),
      }),
    [t],
  );

  const blocker = useCallback(
    async (tx: Transition) => {
      if (await confirmLeave()) tx.retry();
    },
    [confirmLeave],
  );

  useBlocker(blocker, !!file && !exit);

  useEffect(() => {
    if (exit === AGREEMENT_SETUP_EXITS.ACTIVATED) onActivated();
    if (exit === AGREEMENT_SETUP_EXITS.CLOSED) onClose?.();
    if (exit === AGREEMENT_SETUP_EXITS.DOCUSIGN_CONNECTED)
      onDocusignConnected?.();
  }, [exit, onActivated, onClose, onDocusignConnected]);

  const close = async () => {
    if (!file || (await confirmLeave())) setExit(AGREEMENT_SETUP_EXITS.CLOSED);
  };

  const popFileError = useCallback(
    (code: string) =>
      popError(t(AGREEMENT_FILE_ERROR_KEYS[code], AGREEMENT_FILE_LIMIT_PARAMS)),
    [popError, t],
  );

  const changeFile = useCallback((nextFile?: File) => {
    setFile(nextFile);
    setFields([]);
    setInvalidFieldIds([]);
  }, []);

  const selectFile = (selectedFile: File) => {
    if (!isPdf(selectedFile.type)) {
      popError(t('agreementPdfOnly'));
      return;
    }

    if (getIsAgreementFileTooLarge(selectedFile)) {
      popFileError(AGREEMENT_ERROR_CODES.FILE_TOO_LARGE);
      return;
    }

    changeFile(selectedFile);
  };

  useEffect(() => {
    if (initialFile) selectFile(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);

  const onDocumentLoad = useCallback(
    (pageCount: number) => {
      if (getIsAgreementPageCountValid(pageCount)) return;

      popFileError(AGREEMENT_ERROR_CODES.FILE_PAGE_COUNT_INVALID);
      changeFile(undefined);
    },
    [changeFile, popFileError],
  );

  const onActivateError = (error: AxiosError) => {
    const code = getAgreementErrorCode(error);

    if (getIsAgreementFileError(code)) {
      popFileError(code);
      return;
    }

    switch (code) {
      case AGREEMENT_ERROR_CODES.FIELDS_INVALID:
        if (getIsSignatureRequiredError(error)) {
          popError(t('agreementAddSignatureHint'));
          break;
        }

        setInvalidFieldIds(getAgreementInvalidFieldIds(error));
        popError(
          t(
            getIsLabelInvalidError(error)
              ? 'agreementAddFieldLabelHint'
              : 'agreementFieldsInvalidError',
          ),
        );
        break;
      case AGREEMENT_ERROR_CODES.DOCUSIGN_CONNECTED:
        popError(t('agreementDocusignConnectedError'));
        setExit(AGREEMENT_SETUP_EXITS.DOCUSIGN_CONNECTED);
        break;
      default:
        popServerError(error);
    }
  };

  const activateAgreement = async () => {
    try {
      setIsActivating(true);

      await onActivate({ file, fields });

      setExit(AGREEMENT_SETUP_EXITS.ACTIVATED);
    } catch (e) {
      onActivateError(e as AxiosError);
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <AgreementPageLayout
      className={classes}
      title={t('agreementSetUpNda')}
      backPath={backPath}
      backLabel={backLabel}
      onClose={backPath ? undefined : close}
      actions={
        <Button
          type="button"
          className="agreement-setup__activate"
          disabled={isDisabled || !getCanActivateAgreement(file, fields)}
          onClick={activateAgreement}
        >
          {t('agreementActivate')}
        </Button>
      }
    >
      <AgreementFieldEditor
        className="agreement-setup__editor"
        fileUrl={fileUrl}
        fields={fields}
        invalidFieldIds={invalidFieldIds}
        sidebarHeader={
          <AgreementDocumentPanel
            fileName={file?.name}
            isDisabled={isDisabled}
            onFileSelect={selectFile}
            onFileRemove={() => changeFile(undefined)}
          />
        }
        emptyDocument={
          <AgreementPdfDropzone
            isDisabled={isDisabled}
            onFileSelect={selectFile}
          />
        }
        onFieldsChange={setFields}
        onDocumentLoad={onDocumentLoad}
      />
    </AgreementPageLayout>
  );
};

export default AgreementSetup;
